import type { Environment } from '@/core/lib/env/env.context';
import { ACTUAL_ENV, valuesFromProcess } from '@/core/lib/env/env.context';
import { ApiVersions } from '@/core/lib/fetch';
import api from '@/core/lib/fetch/api';
import type { UserBadgesResult } from '@/core/types/badge';
import { userBadgesResultSchema } from '@/core/types/badge';
import { type UsersContactForm, UsersContactsFollowsResult, usersContactsFollowsResultSchema, type UsersContactsResult, usersContactsResultSchema } from '@/core/types/contact';
import type { UsersHasControlResult } from '@/core/types/control';
import { usersHasControlResultSchema } from '@/core/types/control';
import type { UsersActivitiesResult, UsersChangePasswordForm, UsersChangePasswordResult, UsersDeleteAccountForm, UsersPrivateProfilResult, UsersUserAdlessResults } from '@/core/types/profile';
import { usersActivitiesResultSchema, usersPrivateProfilResultSchema, usersPublicProfilResultSchema, usersUserAdlessResults } from '@/core/types/profile';
import type { UserControlCguForm, UserControlEmailCheckForm, UserControlResult, UserControlRsForm, UserControlUsernameForm } from '@/core/types/users';
import { userControlResultSchema } from '@/core/types/users';

const getUrl = () => valuesFromProcess[ACTUAL_ENV as Environment]?.DONNONS_API_URL;

const query = {
  activities: async (token?: string) =>
    api.get<UsersActivitiesResult>({
      url: `${getUrl()}${ApiVersions.V1}/user/activities`,
      schema: usersActivitiesResultSchema,
      token,
    }),
  adless: async (token?: string) =>
    api.get<UsersUserAdlessResults>({
      url: `${getUrl()}${ApiVersions.V1}/user/adless`,
      schema: usersUserAdlessResults,
      token,
    }),
  badges: async (token?: string) =>
    api.get<UserBadgesResult>({
      url: `${getUrl()}${ApiVersions.V1}/user/badges`,
      schema: userBadgesResultSchema,
      token,
    }),
  contacts: async (token: string) =>
    api.get<UsersContactsResult>({
      url: `${getUrl()}${ApiVersions.V1}/user/contacts`,
      schema: usersContactsResultSchema,
      token,
    }),
  contactsFollows: async ({ from, size }: { from: number; size: number }, token: string) =>
    api.get<UsersContactsFollowsResult>({
      url: `${getUrl()}${ApiVersions.V1}/user/contacts/follows?size=${size}&from=${from}`,
      schema: usersContactsFollowsResultSchema,
      token,
    }),
  contactsFollowing: async ({ from, size }: { from: number; size: number }, token: string) =>
    api.get<UsersContactsFollowsResult>({
      url: `${getUrl()}${ApiVersions.V1}/user/contacts/following?size=${size}&from=${from}`,
      schema: usersContactsFollowsResultSchema,
      token,
    }),
  control: async (token: string) =>
    api.get<UserControlResult>({
      url: `${getUrl()}${ApiVersions.V1}/user/control`,
      schema: userControlResultSchema,
      token,
    }),
  get: async (token: string) =>
    api.get<UsersPrivateProfilResult>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      schema: usersPrivateProfilResultSchema,
      token,
    }),
  user: async (uuid: string, token?: string) =>
    api.get<UsersPrivateProfilResult>({
      url: `${getUrl()}${ApiVersions.V1}/user/${uuid}`,
      schema: usersPublicProfilResultSchema,
      token,
    }),
};

export interface MsgParams {
  email: boolean;
  push: boolean;
}

const mutation = {
  accountLifePush: async (life_push: boolean, token: string) =>
    api.patch<null, { life_push: boolean }>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      token,
      data: {
        life_push,
      },
    }),
  accountLifeEmail: async (life_email: boolean, token: string) =>
    api.patch<null, { life_email: boolean }>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      token,
      data: {
        life_email,
      },
    }),
  block: async (uuid: string, token: string) =>
    api.post<null, UsersContactForm>({
      url: `${getUrl()}${ApiVersions.V1}/user/contact/${uuid}`,
      token,
      data: { type: 2 } satisfies UsersContactForm,
    }),
  control: {
    cgu: async (token: string) =>
      api.put<UsersHasControlResult, UserControlCguForm>({
        url: `${getUrl()}${ApiVersions.V1}/user/control/cgu`,
        schema: usersHasControlResultSchema,
        token,
        data: { cgu: true },
      }),
    delete: async (token: string) =>
      api.put<UsersHasControlResult>({
        url: `${getUrl()}${ApiVersions.V1}/user/control/delete`,
        schema: usersHasControlResultSchema,
        token,
        data: undefined,
      }),
    emailCheck: async (otp: string, token: string) =>
      api.put<UsersHasControlResult, UserControlEmailCheckForm>({
        url: `${getUrl()}${ApiVersions.V1}/user/control/email_check`,
        schema: usersHasControlResultSchema,
        token,
        data: { otp },
      }),
    rs: async (data: UserControlRsForm, token: string) =>
      api.put<UsersHasControlResult, UserControlRsForm>({
        url: `${getUrl()}${ApiVersions.V1}/user/control/rs`,
        schema: usersHasControlResultSchema,
        token,
        data,
      }),
    username: async (data: UserControlUsernameForm, token: string) =>
      api.put<UsersHasControlResult, UserControlUsernameForm>({
        url: `${getUrl()}${ApiVersions.V1}/user/control/username`,
        schema: usersHasControlResultSchema,
        token,
        data,
      }),
  },
  deleteAccount: async (data: UsersDeleteAccountForm, token: string) => {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (key !== 'details' && value) {
        params.append('reasons[]', key);
      }
      if (key === 'details' && value && data.details) {
        params.append('other', data.details);
      }
    });
    return api.destroy<null>({
      url: `${getUrl()}${ApiVersions.V1}/user?${params.toString()}`,
      token,
      data: undefined,
    });
  },

  deleteContact: async (uuid: string, token: string) =>
    api.destroy<null>({
      url: `${getUrl()}${ApiVersions.V1}/user/contact/${uuid}`,
      token,
      data: undefined,
    }),
  follow: async (uuid: string, token: string) =>
    api.post<null, UsersContactForm>({
      url: `${getUrl()}${ApiVersions.V1}/user/contact/${uuid}`,
      token,
      data: { type: 1 } satisfies UsersContactForm,
    }),
  msgEmail: async (email: boolean, token: string) =>
    api.patch<null, { msg_email: boolean }>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      token,
      data: {
        msg_email: email,
      },
    }),
  msgPush: async (push: boolean, token: string) =>
    api.patch<null, { msg_push: boolean }>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      token,
      data: {
        msg_push: push,
      },
    }),

  newsletter: async (newsletter: boolean, token: string) =>
    api.patch<null, { newsletter: boolean }>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      token,
      data: {
        newsletter,
      },
    }),
  notifications: async ({ push, newsletter }: { push: boolean; newsletter: boolean }, token: string) =>
    api.patch<null, { push: boolean; newsletter: boolean }>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      token,
      data: {
        push,
        newsletter,
      },
    }),
  push: async (push: boolean, token: string) =>
    api.patch<null, { push: boolean }>({
      url: `${getUrl()}${ApiVersions.V1}/user`,
      token,
      data: {
        push,
      },
    }),
  updatePassword: async (data: UsersChangePasswordForm, token: string) =>
    api.put<UsersChangePasswordResult, UsersChangePasswordForm>({
      url: `${getUrl()}${ApiVersions.V1}/user/password`,
      token,
      data,
    }),
};

export default { mutation, query };
